"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CASETRUST_BUSINESS_FLAG_LABELS = void 0;
exports.casetrustSgtDateParts = casetrustSgtDateParts;
exports.casetrustEndOfSgtDay = casetrustEndOfSgtDay;
exports.casetrustDaysBetween = casetrustDaysBetween;
exports.casetrustIsStaleActivity = casetrustIsStaleActivity;
exports.casetrustDueDateReached = casetrustDueDateReached;
exports.casetrustIsBusinessFlagged = casetrustIsBusinessFlagged;
exports.evaluateCasetrustBusinessFlag = evaluateCasetrustBusinessFlag;
exports.casetrustEarliestInvoiceDueDate = casetrustEarliestInvoiceDueDate;
exports.isCasetrustActiveProject = isCasetrustActiveProject;
exports.casetrustProjectFlagActivityDate = casetrustProjectFlagActivityDate;
exports.isCasetrustProjectFlagged = isCasetrustProjectFlagged;
exports.casetrustNoEscrowActivity = casetrustNoEscrowActivity;
const MS_PER_DAY = 86_400_000;
const CASETRUST_TZ = "Asia/Singapore";
exports.CASETRUST_BUSINESS_FLAG_LABELS = {
    never_transacted: "Never transacted",
    no_recent_activity: "No recent activity",
    healthy: "Healthy",
};
function casetrustSgtDateParts(date) {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: CASETRUST_TZ,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(date);
    const read = (type) => Number(parts.find((part) => part.type === type)?.value);
    return { year: read("year"), month: read("month"), day: read("day") };
}
/** End of the Singapore calendar day that contains `date` (23:59:59.999 SGT). */
function casetrustEndOfSgtDay(date) {
    const { year, month, day } = casetrustSgtDateParts(date);
    return new Date(Date.UTC(year, month - 1, day, 15, 59, 59, 999));
}
/** Whole Singapore calendar days from `earlier` up to `later`. */
function casetrustDaysBetween(later, earlier) {
    const end = casetrustSgtDateParts(later);
    const start = casetrustSgtDateParts(earlier);
    const endUtc = Date.UTC(end.year, end.month - 1, end.day);
    const startUtc = Date.UTC(start.year, start.month - 1, start.day);
    return Math.round((endUtc - startUtc) / MS_PER_DAY);
}
function casetrustIsStaleActivity(activityDate, days, asOf) {
    if (!activityDate)
        return true;
    return casetrustDaysBetween(asOf, activityDate) >= days;
}
/** True when `dueDate` falls on the Singapore calendar day of `asOf` or earlier. */
function casetrustDueDateReached(dueDate, asOf) {
    return casetrustDaysBetween(asOf, dueDate) >= 0;
}
function casetrustIsBusinessFlagged(state) {
    return state !== "healthy";
}
function evaluateCasetrustBusinessFlag(input, days, asOf) {
    const anchor = input.lastProjectCreatedDate ?? input.joinDate;
    if (!input.hasEverMovedMoney) {
        return casetrustIsStaleActivity(anchor, days, asOf) ? "never_transacted" : "healthy";
    }
    if (casetrustIsStaleActivity(anchor, days, asOf)) {
        return "no_recent_activity";
    }
    return "healthy";
}
/** Earliest due date among invoices that can still make a project active. Cancelled and closed rows stay in reporting but are not the current invoice. */
function casetrustEarliestInvoiceDueDate(invoices) {
    let earliest = null;
    for (const invoice of invoices) {
        if (invoice.status === "cancelled" || invoice.status === "closed" || !invoice.dueDate)
            continue;
        if (!earliest || invoice.dueDate < earliest)
            earliest = invoice.dueDate;
    }
    return earliest;
}
/** Accepted in-progress project whose earliest invoice due date has been reached. Pay-in is irrelevant. */
function isCasetrustActiveProject(input, asOf) {
    if (input.status !== "in-progress")
        return false;
    if (!input.earliestDueDate)
        return false;
    return casetrustDueDateReached(input.earliestDueDate, asOf);
}
/** Phase 1 display anchor: last release, otherwise client acceptance. */
function casetrustProjectFlagActivityDate(input) {
    return input.lastReleaseDate ?? input.createdDate;
}
/**
 * Layer 2 Phase 1. Flags only after a release, when escrow is empty and the last release is outside the window.
 * Dispute does not suppress the flag. Never-released projects stay off.
 */
function isCasetrustProjectFlagged(input, days, asOf) {
    if (input.status === "completed")
        return false;
    if (input.inEscrow > 0)
        return false;
    if (input.lastReleaseDate === null)
        return false;
    return casetrustIsStaleActivity(input.lastReleaseDate, days, asOf);
}
function casetrustNoEscrowActivity(projects, days, asOf) {
    const inProgress = projects.filter((project) => project.status === "in-progress");
    if (!inProgress.length)
        return false;
    return inProgress.every((project) => project.lastReleaseDate === null || casetrustIsStaleActivity(project.lastReleaseDate, days, asOf));
}
